import { useState, useCallback, type FormEvent } from 'react'
import { api, type ApiError } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { DoCaptcha, hasDoCaptchaKey } from '../components/DoCaptcha'
import { AuthLayout } from '../components/auth/AuthLayout'
import { FloatingInput } from '../components/ui/input'
import { Button, ButtonLink } from '../components/ui/button'
import { StatusBox } from '../components/ui/badge'

export function LoginPage() {
  const setSession = useAuthStore((s) => s.setSession)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onCaptchaVerify = useCallback((token: string) => setCaptchaToken(token), [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (hasDoCaptchaKey() && !captchaToken) {
      setError('Please complete the CAPTCHA verification.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const auth = await api.login({ email, password, captchaToken: captchaToken ?? undefined })
      setSession(auth)
      navigate('/dashboard')
    } catch (err) {
      setError((err as ApiError).title ?? 'Invalid email or password.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue building."
      panel={{ line1: 'Build apps with natural language.', line2: 'Describe it. Claude writes it. You deploy it.' }}
      footer={
        <p className="text-center">
          New to Doable?{' '}
          <ButtonLink
            variant="link"
            size="sm"
            className="h-auto px-0 text-sm"
            href="/signup"
            onClick={(e) => { e.preventDefault(); navigate('/signup') }}
          >
            Create account
          </ButtonLink>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <FloatingInput
          label="Email"
          type="email"
          size="auth"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <FloatingInput
          label="Password"
          type="password"
          size="auth"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />

        <div className="flex justify-end">
          <ButtonLink
            variant="link"
            size="sm"
            className="h-auto px-0 text-sm text-muted-foreground hover:text-foreground"
            href="/forgot-password"
            onClick={(e) => { e.preventDefault(); navigate('/forgot-password') }}
          >
            Forgot password?
          </ButtonLink>
        </div>

        <DoCaptcha onVerify={onCaptchaVerify} />

        {error && <StatusBox tone="destructive">{error}</StatusBox>}

        <Button type="submit" size="auth" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  )
}
